import dayjs from 'dayjs';
import type {
  AdminAnalytics,
  AdminDailyActivity,
  AdminPeakCell,
  AdminTaskFlowDay,
} from '../../../../services/dashboardService';
import type { ScopeWindow } from '../../dashboard/utils/scopeWindow';

/* ============================================================
 * Admin metrics engine — every number on the Admin Analytics
 * dashboard is computed here from the raw daily slices, never
 * hardcoded. All functions are pure so the dictionary is testable.
 * ============================================================ */

export type Granularity = 'daily' | 'weekly' | 'monthly';

export const GRANULARITIES: Array<{ key: Granularity; label: string }> = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
];

/** Drill scope applied to every metric. */
export interface AdminDrill {
  state: string | null;
  locationId: string | null;
  projectId: string | null;
  engineerId: string | null;
}

const DAY = 'YYYY-MM-DD';

/** Monday of the week containing d (business weeks run Mon–Sun). */
const mondayOf = (d: dayjs.Dayjs) => d.subtract((d.day() + 6) % 7, 'day');

export function bucketKey(date: string, g: Granularity): string {
  const d = dayjs(date);
  if (g === 'daily') return d.format(DAY);
  if (g === 'weekly') return mondayOf(d).format(DAY);
  return d.startOf('month').format(DAY);
}

export function bucketLabel(key: string, g: Granularity): string {
  const d = dayjs(key);
  if (g === 'daily') return d.format('D MMM');
  if (g === 'weekly') return `wk ${d.format('D MMM')}`;
  return d.format('MMM YYYY');
}

/** Every bucket covering the window — empty periods render as honest zeros. */
export function listBuckets(window: ScopeWindow, g: Granularity): string[] {
  const keys: string[] = [];
  let cursor = dayjs(bucketKey(window.from, g));
  const end = dayjs(window.to);
  while (!cursor.isAfter(end, 'day')) {
    keys.push(cursor.format(DAY));
    cursor =
      g === 'daily' ? cursor.add(1, 'day') : g === 'weekly' ? cursor.add(7, 'day') : cursor.add(1, 'month');
  }
  return keys;
}

/* ---------------- scope filtering ---------------- */

export interface ScopedData {
  projectIds: Set<string>;
  activity: AdminDailyActivity[];
  flow: AdminTaskFlowDay[];
  /** Engineers with any activity in scope (drives peak + adoption). */
  engineerIds: Set<string>;
}

export function scopeData(payload: AdminAnalytics, drill: AdminDrill): ScopedData {
  const projectIds = new Set(
    payload.projects
      .filter(
        (p) =>
          (!drill.state || (p.state ?? 'Unmapped') === drill.state) &&
          (!drill.locationId || (p.locationId ?? 'unmapped') === drill.locationId) &&
          (!drill.projectId || p.projectId === drill.projectId)
      )
      .map((p) => p.projectId)
  );
  const activity = payload.dailyActivity.filter(
    (a) => projectIds.has(a.projectId) && (!drill.engineerId || a.engineerId === drill.engineerId)
  );
  const flow = drill.engineerId ? [] : payload.taskFlow.filter((f) => projectIds.has(f.projectId));
  return {
    projectIds,
    activity,
    flow,
    engineerIds: new Set(activity.map((a) => a.engineerId)),
  };
}

/* ---------------- adoption ---------------- */

export interface AdoptionPoint {
  bucket: string;
  label: string;
  /** Unique active engineers in the bucket. */
  active: number;
}

/** Unique active engineers per bucket at the chosen granularity. */
export function activeSeries(activity: AdminDailyActivity[], window: ScopeWindow, g: Granularity): AdoptionPoint[] {
  const byBucket = new Map<string, Set<string>>();
  for (const row of activity) {
    const key = bucketKey(row.date, g);
    const set = byBucket.get(key) ?? new Set<string>();
    set.add(row.engineerId);
    byBucket.set(key, set);
  }
  return listBuckets(window, g).map((key) => ({
    bucket: key,
    label: bucketLabel(key, g),
    active: byBucket.get(key)?.size ?? 0,
  }));
}

export interface AdoptionSummary {
  /** Unique engineers active in the last 7 days of the window. */
  wau: number;
  /** Unique engineers active in the last 30 days of the window. */
  mau: number;
  /** Average daily uniques across the window. */
  avgDau: number;
  /** avgDau ÷ mau, 0–1 (null when mau = 0). */
  stickiness: number | null;
}

export function adoptionSummary(activity: AdminDailyActivity[], window: ScopeWindow): AdoptionSummary {
  const end = dayjs(window.to);
  const wauStart = end.subtract(6, 'day');
  const mauStart = end.subtract(29, 'day');
  const wauSet = new Set<string>();
  const mauSet = new Set<string>();
  const byDay = new Map<string, Set<string>>();
  for (const row of activity) {
    const d = dayjs(row.date);
    if (!d.isBefore(wauStart, 'day') && !d.isAfter(end, 'day')) wauSet.add(row.engineerId);
    if (!d.isBefore(mauStart, 'day') && !d.isAfter(end, 'day')) mauSet.add(row.engineerId);
    const set = byDay.get(row.date) ?? new Set<string>();
    set.add(row.engineerId);
    byDay.set(row.date, set);
  }
  const days = Math.max(dayjs(window.to).diff(dayjs(window.from), 'day') + 1, 1);
  const avgDau = [...byDay.values()].reduce((s, set) => s + set.size, 0) / days;
  return {
    wau: wauSet.size,
    mau: mauSet.size,
    avgDau,
    stickiness: mauSet.size > 0 ? avgDau / mauSet.size : null,
  };
}

/* ---------------- activation funnel + cohorts ---------------- */

export interface FunnelStage {
  key: string;
  label: string;
  value: number;
  /** Denominator of the conversion % (previous stage). */
  of: number;
  note?: string;
}

/**
 * Activation funnel. "First login" is not tracked (no session events) —
 * the stage is omitted and recorded in the Instrumentation Appendix.
 * Weekly habit (active ≥ 3 of first 4 weeks) is only decidable for
 * engineers whose first 4 weeks fall inside the fetched activity window.
 */
export function activationFunnel(payload: AdminAnalytics, window: ScopeWindow): FunnelStage[] {
  const onboarded = payload.engineers;
  const submitted = onboarded.filter((e) => e.firstSubmissionAt !== null);
  const activated = submitted.filter(
    (e) => dayjs(e.firstSubmissionAt as string).diff(dayjs(e.onboardedAt), 'day') <= 7
  );

  const activityByEngineer = new Map<string, string[]>();
  for (const row of payload.dailyActivity) {
    const list = activityByEngineer.get(row.engineerId) ?? [];
    list.push(row.date);
    activityByEngineer.set(row.engineerId, list);
  }
  const windowStart = dayjs(window.from);
  const decidable = activated.filter((e) => !dayjs(e.onboardedAt).isBefore(windowStart, 'day'));
  const habit = decidable.filter((e) => {
    const onboardedAt = dayjs(e.onboardedAt);
    const dates = activityByEngineer.get(e.engineerId) ?? [];
    const weeksActive = new Set(
      dates
        .map((d) => Math.floor(dayjs(d).diff(onboardedAt, 'day') / 7))
        .filter((w) => w >= 0 && w < 4)
    );
    return weeksActive.size >= 3;
  });

  return [
    { key: 'onboarded', label: 'Onboarded', value: onboarded.length, of: onboarded.length },
    { key: 'first-submission', label: 'First submission', value: submitted.length, of: onboarded.length },
    { key: 'activated', label: 'Activated ≤ 7 days', value: activated.length, of: submitted.length },
    {
      key: 'weekly-habit',
      label: 'Weekly habit',
      value: habit.length,
      of: decidable.length,
      note:
        decidable.length < activated.length
          ? `decidable for ${decidable.length} of ${activated.length} activated (needs the first 4 weeks inside the window)`
          : undefined,
    },
  ];
}

export interface CohortRow {
  cohort: string;
  label: string;
  size: number;
  /** % active in week N since onboarding; null = outside the data window. */
  weeks: Array<number | null>;
}

/** Retention grid: rows = onboarding-month cohorts, cols = weeks since. */
export function retentionCohorts(payload: AdminAnalytics, window: ScopeWindow, weekCount = 8): CohortRow[] {
  const activityByEngineer = new Map<string, string[]>();
  for (const row of payload.dailyActivity) {
    const list = activityByEngineer.get(row.engineerId) ?? [];
    list.push(row.date);
    activityByEngineer.set(row.engineerId, list);
  }
  const windowStart = dayjs(window.from);
  const windowEnd = dayjs(window.to);

  const cohorts = new Map<string, typeof payload.engineers>();
  for (const engineer of payload.engineers) {
    const key = dayjs(engineer.onboardedAt).startOf('month').format(DAY);
    const list = cohorts.get(key) ?? [];
    list.push(engineer);
    cohorts.set(key, list);
  }

  return [...cohorts.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .slice(0, 6)
    .map(([cohort, members]) => {
      const weeks: Array<number | null> = [];
      for (let w = 0; w < weekCount; w++) {
        let observable = 0;
        let active = 0;
        for (const member of members) {
          const weekStart = dayjs(member.onboardedAt).add(w * 7, 'day');
          const weekEnd = weekStart.add(6, 'day');
          // The whole week must sit inside the fetched activity window,
          // otherwise the cell is unknowable — render as —, never 0.
          if (weekStart.isBefore(windowStart, 'day') || weekEnd.isAfter(windowEnd, 'day')) continue;
          observable += 1;
          const dates = activityByEngineer.get(member.engineerId) ?? [];
          if (
            dates.some((d) => {
              const day = dayjs(d);
              return !day.isBefore(weekStart, 'day') && !day.isAfter(weekEnd, 'day');
            })
          ) {
            active += 1;
          }
        }
        weeks.push(observable > 0 ? Math.round((active / observable) * 100) : null);
      }
      return {
        cohort,
        label: dayjs(cohort).format('MMM YYYY'),
        size: members.length,
        weeks,
      };
    });
}

/* ---------------- usage momentum ---------------- */

export type UsageClass = 'growing' | 'steady' | 'declining' | 'dormant';

export const USAGE_META: Record<UsageClass, { label: string }> = {
  growing: { label: 'Growing' },
  steady: { label: 'Steady' },
  declining: { label: 'Declining' },
  dormant: { label: 'Dormant' },
};

export interface EngineerMomentum {
  engineerId: string;
  name: string;
  roleName: string;
  usage: UsageClass;
  /** % change of second-half vs first-half actions (null for dormant-all-window). */
  changePct: number | null;
  /** Weekly action counts across the window (sparkline). */
  weekly: number[];
  actions: number;
  completed: number;
  skipped: number;
  lastActiveAt: string | null;
  daysSinceActive: number | null;
  onboardedAt: string;
}

export const DORMANT_DAYS = 14;

/**
 * Classify every engineer's app-usage trend inside the window:
 * Growing ≥ +15% · Steady ±15% · Declining ≤ −15% (second-half vs
 * first-half actions) · Dormant = no action in the last 14 days.
 * `includeInactive` appends zero-activity engineers as Dormant — true for
 * the portfolio-wide roster, false when a drill narrows the scope (an
 * engineer who never worked that project isn't "dormant on it").
 */
export function usageMomentum(
  payload: AdminAnalytics,
  scoped: ScopedData,
  window: ScopeWindow,
  includeInactive = true
): EngineerMomentum[] {
  const windowStart = dayjs(window.from);
  const windowEnd = dayjs(window.to);
  const totalDays = windowEnd.diff(windowStart, 'day') + 1;
  const weekCount = Math.max(Math.ceil(totalDays / 7), 1);

  const byEngineer = new Map<string, AdminDailyActivity[]>();
  for (const row of scoped.activity) {
    const list = byEngineer.get(row.engineerId) ?? [];
    list.push(row);
    byEngineer.set(row.engineerId, list);
  }

  const engineerMeta = new Map(payload.engineers.map((e) => [e.engineerId, e]));

  const rows: EngineerMomentum[] = [];
  for (const [engineerId, activity] of byEngineer) {
    const meta = engineerMeta.get(engineerId);
    const weekly = new Array<number>(weekCount).fill(0);
    let actions = 0;
    let completed = 0;
    let skipped = 0;
    let lastActive: dayjs.Dayjs | null = null;
    for (const row of activity) {
      const d = dayjs(row.date);
      const week = Math.min(Math.floor(d.diff(windowStart, 'day') / 7), weekCount - 1);
      if (week >= 0) weekly[week] = (weekly[week] ?? 0) + row.actions;
      actions += row.actions;
      completed += row.completed;
      skipped += row.skipped;
      if (!lastActive || d.isAfter(lastActive)) lastActive = d;
    }
    const half = Math.floor(weekCount / 2);
    const firstHalf = weekly.slice(0, half).reduce((s, n) => s + n, 0);
    const secondHalf = weekly.slice(weekCount - half).reduce((s, n) => s + n, 0);
    const changePct =
      half === 0 ? null : Math.round(((secondHalf - firstHalf) / Math.max(firstHalf, 1)) * 100);

    const daysSinceActive = lastActive ? windowEnd.diff(lastActive, 'day') : null;
    let usage: UsageClass;
    if (actions === 0 || daysSinceActive === null || daysSinceActive >= DORMANT_DAYS) usage = 'dormant';
    else if (changePct !== null && changePct >= 15) usage = 'growing';
    else if (changePct !== null && changePct <= -15) usage = 'declining';
    else usage = 'steady';

    rows.push({
      engineerId,
      name: meta?.name ?? 'Unknown engineer',
      roleName: meta?.roleName ?? '',
      usage,
      changePct,
      weekly,
      actions,
      completed,
      skipped,
      lastActiveAt: lastActive ? lastActive.format(DAY) : (meta?.lastActiveAt ?? null),
      daysSinceActive,
      onboardedAt: meta?.onboardedAt ?? '',
    });
  }

  // Engineers with zero activity in the window are dormant too — they must
  // not silently vanish from the roster.
  for (const engineer of payload.engineers) {
    if (!includeInactive) break;
    if (byEngineer.has(engineer.engineerId)) continue;
    rows.push({
      engineerId: engineer.engineerId,
      name: engineer.name,
      roleName: engineer.roleName,
      usage: 'dormant',
      changePct: null,
      weekly: new Array<number>(weekCount).fill(0),
      actions: 0,
      completed: 0,
      skipped: 0,
      lastActiveAt: engineer.lastActiveAt,
      daysSinceActive: engineer.lastActiveAt
        ? windowEnd.diff(dayjs(engineer.lastActiveAt), 'day')
        : null,
      onboardedAt: engineer.onboardedAt,
    });
  }

  const order: Record<UsageClass, number> = { growing: 0, steady: 1, declining: 2, dormant: 3 };
  return rows.sort(
    (a, b) => order[a.usage] - order[b.usage] || (b.changePct ?? -1e9) - (a.changePct ?? -1e9) || b.actions - a.actions
  );
}

/* ---------------- peak pulse ---------------- */

export interface PeakMatrix {
  /** cells[dow-1][hour], Mon=index 0. */
  cells: number[][];
  max: number;
  total: number;
  peak: { dow: number; hour: number; actions: number } | null;
  quietestDay: { dow: number; actions: number } | null;
}

export const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function buildPeakMatrix(cells: AdminPeakCell[]): PeakMatrix {
  const grid: number[][] = Array.from({ length: 7 }, () => new Array<number>(24).fill(0));
  let max = 0;
  let total = 0;
  let peak: PeakMatrix['peak'] = null;
  for (const cell of cells) {
    const row = grid[cell.dow - 1];
    if (!row || cell.hour < 0 || cell.hour > 23) continue;
    row[cell.hour] = (row[cell.hour] ?? 0) + cell.actions;
    total += cell.actions;
  }
  const dayTotals = new Array<number>(7).fill(0);
  for (let d = 0; d < 7; d++) {
    for (let h = 0; h < 24; h++) {
      const v = grid[d]?.[h] ?? 0;
      dayTotals[d] = (dayTotals[d] ?? 0) + v;
      if (v > max) {
        max = v;
        peak = { dow: d + 1, hour: h, actions: v };
      }
    }
  }
  let quietestDay: PeakMatrix['quietestDay'] = null;
  if (total > 0) {
    let minIdx = 0;
    for (let d = 1; d < 7; d++) {
      if ((dayTotals[d] ?? 0) < (dayTotals[minIdx] ?? 0)) minIdx = d;
    }
    quietestDay = { dow: minIdx + 1, actions: dayTotals[minIdx] ?? 0 };
  }
  return { cells: grid, max, total, peak, quietestDay };
}

/* ---------------- task flow ---------------- */

export interface FlowPoint {
  bucket: string;
  label: string;
  created: number;
  resolvedCompleted: number;
  resolvedSkipped: number;
  /** created − resolved; positive = backlog growing. */
  netFlow: number;
  scheduled: number;
  scheduledCompleted: number;
  scheduledSkipped: number;
  scheduledPending: number;
  /** Rate trio, % of scheduled — always sums to 100 when scheduled > 0. */
  completionPct: number | null;
  skipPct: number | null;
  pendingPct: number | null;
}

export function taskFlowSeries(flow: AdminTaskFlowDay[], window: ScopeWindow, g: Granularity): FlowPoint[] {
  const byBucket = new Map<string, FlowPoint>();
  const blank = (key: string): FlowPoint => ({
    bucket: key,
    label: bucketLabel(key, g),
    created: 0,
    resolvedCompleted: 0,
    resolvedSkipped: 0,
    netFlow: 0,
    scheduled: 0,
    scheduledCompleted: 0,
    scheduledSkipped: 0,
    scheduledPending: 0,
    completionPct: null,
    skipPct: null,
    pendingPct: null,
  });
  for (const key of listBuckets(window, g)) byBucket.set(key, blank(key));
  for (const row of flow) {
    const key = bucketKey(row.date, g);
    const point = byBucket.get(key);
    if (!point) continue;
    point.created += row.created;
    point.resolvedCompleted += row.resolvedCompleted;
    point.resolvedSkipped += row.resolvedSkipped;
    point.scheduled += row.scheduled;
    point.scheduledCompleted += row.scheduledCompleted;
    point.scheduledSkipped += row.scheduledSkipped;
  }
  for (const point of byBucket.values()) {
    point.scheduledPending = Math.max(point.scheduled - point.scheduledCompleted - point.scheduledSkipped, 0);
    point.netFlow = point.created - point.resolvedCompleted - point.resolvedSkipped;
    if (point.scheduled > 0) {
      point.completionPct = Math.round((point.scheduledCompleted / point.scheduled) * 100);
      point.skipPct = Math.round((point.scheduledSkipped / point.scheduled) * 100);
      // Pending closes the trio so the three always sum to exactly 100.
      point.pendingPct = 100 - point.completionPct - point.skipPct;
    }
  }
  return [...byBucket.values()];
}

export interface FlowTotals {
  created: number;
  scheduled: number;
  scheduledCompleted: number;
  scheduledSkipped: number;
  scheduledPending: number;
  resolved: number;
  netFlow: number;
  completionPct: number | null;
  skipPct: number | null;
  pendingPct: number | null;
}

export function flowTotals(points: FlowPoint[]): FlowTotals {
  const t = points.reduce(
    (acc, p) => {
      acc.created += p.created;
      acc.scheduled += p.scheduled;
      acc.scheduledCompleted += p.scheduledCompleted;
      acc.scheduledSkipped += p.scheduledSkipped;
      acc.resolved += p.resolvedCompleted + p.resolvedSkipped;
      return acc;
    },
    { created: 0, scheduled: 0, scheduledCompleted: 0, scheduledSkipped: 0, resolved: 0 }
  );
  const scheduledPending = Math.max(t.scheduled - t.scheduledCompleted - t.scheduledSkipped, 0);
  const completionPct = t.scheduled > 0 ? Math.round((t.scheduledCompleted / t.scheduled) * 100) : null;
  const skipPct = t.scheduled > 0 ? Math.round((t.scheduledSkipped / t.scheduled) * 100) : null;
  return {
    ...t,
    scheduledPending,
    netFlow: t.created - t.resolved,
    completionPct,
    skipPct,
    pendingPct: completionPct !== null && skipPct !== null ? 100 - completionPct - skipPct : null,
  };
}

/** Median cycle time (created → completed) per bucket, in hours. */
export function cycleSeries(
  samples: Array<{ date: string; hours: number }>,
  window: ScopeWindow,
  g: Granularity
): Array<{ bucket: string; label: string; medianHours: number | null; n: number }> {
  const byBucket = new Map<string, number[]>();
  for (const sample of samples) {
    const key = bucketKey(sample.date, g);
    const list = byBucket.get(key) ?? [];
    list.push(sample.hours);
    byBucket.set(key, list);
  }
  return listBuckets(window, g).map((key) => {
    const list = (byBucket.get(key) ?? []).sort((a, b) => a - b);
    const mid = Math.floor(list.length / 2);
    const median =
      list.length === 0
        ? null
        : list.length % 2 === 1
          ? (list[mid] ?? null)
          : ((list[mid - 1] ?? 0) + (list[mid] ?? 0)) / 2;
    return { bucket: key, label: bucketLabel(key, g), medianHours: median, n: list.length };
  });
}

/* ---------------- growth ---------------- */

export interface GrowthPoint {
  bucket: string;
  label: string;
  newProjects: number;
  cumulativeProjects: number;
  capacityMW: number;
  engineersOnboarded: number;
}

export function growthSeries(payload: AdminAnalytics, window: ScopeWindow, g: Granularity, drill: AdminDrill): GrowthPoint[] {
  const projects = payload.projects.filter(
    (p) =>
      (!drill.state || (p.state ?? 'Unmapped') === drill.state) &&
      (!drill.locationId || (p.locationId ?? 'unmapped') === drill.locationId) &&
      (!drill.projectId || p.projectId === drill.projectId)
  );
  const windowStart = dayjs(window.from);
  const baseline = projects.filter((p) => dayjs(p.createdAt).isBefore(windowStart, 'day')).length;

  const byBucket = new Map<string, GrowthPoint>();
  for (const key of listBuckets(window, g)) {
    byBucket.set(key, {
      bucket: key,
      label: bucketLabel(key, g),
      newProjects: 0,
      cumulativeProjects: 0,
      capacityMW: 0,
      engineersOnboarded: 0,
    });
  }
  const windowEnd = dayjs(window.to);
  for (const project of projects) {
    const created = dayjs(project.createdAt);
    if (created.isBefore(windowStart, 'day') || created.isAfter(windowEnd, 'day')) continue;
    const point = byBucket.get(bucketKey(created.format(DAY), g));
    if (!point) continue;
    point.newProjects += 1;
    point.capacityMW += project.capacityMW;
  }
  for (const engineer of payload.engineers) {
    const onboarded = dayjs(engineer.onboardedAt);
    if (onboarded.isBefore(windowStart, 'day') || onboarded.isAfter(windowEnd, 'day')) continue;
    const point = byBucket.get(bucketKey(onboarded.format(DAY), g));
    if (point) point.engineersOnboarded += 1;
  }
  let running = baseline;
  const points = [...byBucket.values()];
  for (const point of points) {
    running += point.newProjects;
    point.cumulativeProjects = running;
  }
  return points;
}

/* ---------------- drill-down breakdown ---------------- */

export type BreakdownKind = 'state' | 'location' | 'project' | 'engineer';

export interface BreakdownEntry {
  key: string;
  kind: BreakdownKind;
  name: string;
  created: number;
  scheduled: number;
  completed: number;
  skipped: number;
  pending: number;
  completionPct: number | null;
  skipPct: number | null;
  pendingPct: number | null;
  activeEngineers: number;
  /** Completion-rate points vs previous window (null without prior data). */
  deltaCompletionPct: number | null;
}

const LEVEL_CHILD: Record<number, { kind: BreakdownKind; label: string }> = {
  1: { kind: 'state', label: 'States' },
  2: { kind: 'location', label: 'Locations' },
  3: { kind: 'project', label: 'Projects' },
  4: { kind: 'engineer', label: 'Engineers' },
  5: { kind: 'engineer', label: 'Engineers' },
};

function groupKeyOf(kind: BreakdownKind, payload: AdminAnalytics) {
  const byProject = new Map(payload.projects.map((p) => [p.projectId, p]));
  return (projectId: string): { key: string; name: string } => {
    const project = byProject.get(projectId);
    if (kind === 'state') {
      const state = project?.state ?? 'Unmapped';
      return { key: state, name: state };
    }
    if (kind === 'location') {
      return {
        key: project?.locationId ?? 'unmapped',
        name: project?.locationName ?? 'Unmapped location',
      };
    }
    return { key: projectId, name: project?.name ?? 'Unknown project' };
  };
}

/**
 * Task-flow breakdown for the CHILD level of the current drill — the same
 * anatomy at every level. Engineer rows come from activity (created /
 * scheduled are project-level facts and stay null-rated there).
 */
export function breakdownByLevel(
  current: AdminAnalytics,
  previous: AdminAnalytics | null,
  drill: AdminDrill,
  level: number
): { label: string; kind: BreakdownKind; entries: BreakdownEntry[] } {
  const child = LEVEL_CHILD[Math.min(level, 4)] ?? LEVEL_CHILD[1]!;
  const scoped = scopeData(current, drill);
  const prevScoped = previous ? scopeData(previous, drill) : null;

  if (child.kind === 'engineer') {
    const engineerName = new Map(current.engineers.map((e) => [e.engineerId, e.name]));
    const byEngineer = new Map<string, BreakdownEntry>();
    for (const row of scoped.activity) {
      let entry = byEngineer.get(row.engineerId);
      if (!entry) {
        entry = {
          key: row.engineerId,
          kind: 'engineer',
          name: engineerName.get(row.engineerId) ?? 'Unknown engineer',
          created: 0,
          scheduled: 0,
          completed: 0,
          skipped: 0,
          pending: 0,
          completionPct: null,
          skipPct: null,
          pendingPct: null,
          activeEngineers: 1,
          deltaCompletionPct: null,
        };
        byEngineer.set(row.engineerId, entry);
      }
      entry.completed += row.completed;
      entry.skipped += row.skipped;
      entry.created += row.actions;
    }
    return {
      label: child.label,
      kind: 'engineer',
      entries: [...byEngineer.values()].sort((a, b) => b.completed - a.completed),
    };
  }

  const keyOf = groupKeyOf(child.kind, current);
  const prevKeyOf = previous ? groupKeyOf(child.kind, previous) : null;

  const fold = (flow: AdminTaskFlowDay[], keyFn: (projectId: string) => { key: string; name: string }) => {
    const groups = new Map<string, { name: string; created: number; scheduled: number; completed: number; skipped: number }>();
    for (const row of flow) {
      const { key, name } = keyFn(row.projectId);
      let group = groups.get(key);
      if (!group) {
        group = { name, created: 0, scheduled: 0, completed: 0, skipped: 0 };
        groups.set(key, group);
      }
      group.created += row.created;
      group.scheduled += row.scheduled;
      group.completed += row.scheduledCompleted;
      group.skipped += row.scheduledSkipped;
    }
    return groups;
  };

  const nowGroups = fold(scoped.flow, keyOf);
  const prevGroups = prevScoped && prevKeyOf ? fold(prevScoped.flow, prevKeyOf) : null;

  const engineersByGroup = new Map<string, Set<string>>();
  for (const row of scoped.activity) {
    const { key } = keyOf(row.projectId);
    const set = engineersByGroup.get(key) ?? new Set<string>();
    set.add(row.engineerId);
    engineersByGroup.set(key, set);
  }

  const entries: BreakdownEntry[] = [...nowGroups.entries()].map(([key, group]) => {
    const pending = Math.max(group.scheduled - group.completed - group.skipped, 0);
    const completionPct = group.scheduled > 0 ? Math.round((group.completed / group.scheduled) * 100) : null;
    const skipPct = group.scheduled > 0 ? Math.round((group.skipped / group.scheduled) * 100) : null;
    const prev = prevGroups?.get(key);
    const prevCompletion = prev && prev.scheduled > 0 ? Math.round((prev.completed / prev.scheduled) * 100) : null;
    return {
      key,
      kind: child.kind,
      name: group.name,
      created: group.created,
      scheduled: group.scheduled,
      completed: group.completed,
      skipped: group.skipped,
      pending,
      completionPct,
      skipPct,
      pendingPct: completionPct !== null && skipPct !== null ? 100 - completionPct - skipPct : null,
      activeEngineers: engineersByGroup.get(key)?.size ?? 0,
      deltaCompletionPct:
        completionPct !== null && prevCompletion !== null ? completionPct - prevCompletion : null,
    };
  });

  entries.sort((a, b) => (b.completionPct ?? -1) - (a.completionPct ?? -1) || b.scheduled - a.scheduled);
  return { label: child.label, kind: child.kind, entries };
}

/* ---------------- insights ---------------- */

export type InsightSeverity = 'critical' | 'warning' | 'info';

export interface AdminInsight {
  id: string;
  severity: InsightSeverity;
  sentence: string;
  /** URL params that open the proving view. */
  link: Partial<Record<'state' | 'site' | 'project' | 'engineer' | 'g', string>>;
}

export interface InsightInputs {
  current: { payload: AdminAnalytics; window: ScopeWindow };
  previous: { payload: AdminAnalytics; window: ScopeWindow };
}

/** Rules-based anomaly feed (spec §6.9) — each card links to its proof. */
export function deriveInsights({ current, previous }: InsightInputs): AdminInsight[] {
  const insights: AdminInsight[] = [];
  const all: AdminDrill = { state: null, locationId: null, projectId: null, engineerId: null };
  const nowScoped = scopeData(current.payload, all);
  const prevScoped = scopeData(previous.payload, all);

  // Skip rate ≥ 2× the prior window (with a floor so 0→1 doesn't scream).
  const nowFlow = flowTotals(taskFlowSeries(nowScoped.flow, current.window, 'weekly'));
  const prevFlow = flowTotals(taskFlowSeries(prevScoped.flow, previous.window, 'weekly'));
  if (
    nowFlow.skipPct !== null &&
    prevFlow.skipPct !== null &&
    nowFlow.skipPct >= 5 &&
    nowFlow.skipPct >= prevFlow.skipPct * 2
  ) {
    insights.push({
      id: 'skip-rate-doubled',
      severity: 'critical',
      sentence: `Skip rate is ${nowFlow.skipPct}% of ${nowFlow.scheduled.toLocaleString('en-IN')} scheduled — at least double the prior window's ${prevFlow.skipPct}%.`,
      link: {},
    });
  }

  // WAU drop ≥ 20%.
  const nowAdoption = adoptionSummary(nowScoped.activity, current.window);
  const prevAdoption = adoptionSummary(prevScoped.activity, previous.window);
  if (prevAdoption.wau > 0 && nowAdoption.wau <= prevAdoption.wau * 0.8) {
    insights.push({
      id: 'wau-drop',
      severity: 'critical',
      sentence: `Weekly active engineers fell to ${nowAdoption.wau} from ${prevAdoption.wau} — a ${Math.round(
        (1 - nowAdoption.wau / prevAdoption.wau) * 100
      )}% drop.`,
      link: {},
    });
  }

  // A state's created-tasks spike ≥ 50% vs the prior window.
  const statesOf = (payload: AdminAnalytics, window: ScopeWindow) => {
    const perState = new Map<string, number>();
    const stateByProject = new Map(payload.projects.map((p) => [p.projectId, p.state ?? 'Unmapped']));
    for (const row of payload.taskFlow) {
      const state = stateByProject.get(row.projectId) ?? 'Unmapped';
      perState.set(state, (perState.get(state) ?? 0) + row.created);
    }
    void window;
    return perState;
  };
  const nowStates = statesOf(current.payload, current.window);
  const prevStates = statesOf(previous.payload, previous.window);
  for (const [state, created] of nowStates) {
    const prev = prevStates.get(state) ?? 0;
    if (prev >= 20 && created >= prev * 1.5) {
      insights.push({
        id: `created-spike-${state}`,
        severity: 'warning',
        sentence: `${state} created ${created.toLocaleString('en-IN')} tasks this window — up ${Math.round(
          (created / prev - 1) * 100
        )}% on the prior ${prev.toLocaleString('en-IN')}.`,
        link: { state },
      });
    }
  }

  // ≥ 3 engineers newly dormant.
  const nowMomentum = usageMomentum(current.payload, nowScoped, current.window);
  const prevMomentum = usageMomentum(previous.payload, prevScoped, previous.window);
  const prevDormant = new Set(prevMomentum.filter((m) => m.usage === 'dormant').map((m) => m.engineerId));
  const newlyDormant = nowMomentum.filter((m) => m.usage === 'dormant' && !prevDormant.has(m.engineerId));
  if (newlyDormant.length >= 3) {
    insights.push({
      id: 'newly-dormant',
      severity: 'warning',
      sentence: `${newlyDormant.length} engineers went dormant this window (no app activity for ${DORMANT_DAYS}+ days): ${newlyDormant
        .slice(0, 3)
        .map((m) => m.name)
        .join(', ')}${newlyDormant.length > 3 ? '…' : ''}.`,
      link: {},
    });
  }

  // Newest cohort activation < 50%.
  const funnel = activationFunnel(current.payload, current.window);
  const activatedStage = funnel.find((s) => s.key === 'activated');
  if (activatedStage && activatedStage.of >= 4 && activatedStage.value / activatedStage.of < 0.5) {
    insights.push({
      id: 'low-activation',
      severity: 'warning',
      sentence: `Only ${activatedStage.value} of ${activatedStage.of} engineers with a first submission activated within 7 days of onboarding.`,
      link: {},
    });
  }

  const severityRank: Record<InsightSeverity, number> = { critical: 0, warning: 1, info: 2 };
  return insights.sort((a, b) => severityRank[a.severity] - severityRank[b.severity]);
}
